import { useEffect, useState, type RefObject } from "react";

export const BOOKING_DISPLAY_GRACE_MS = 10_000;

export type BookingDisplayStatus = "inactive" | "waiting" | "visible" | "blocked";

export type BookingFrameInspection = {
  visible: boolean;
  reason: "missing" | "hidden-style" | "zero-size" | "offscreen-style" | "visible";
};

/**
 * Inspect the rendered frame itself. HighLevel leaves its initialization
 * attribute set even after showing the frame, so that attribute is purposely
 * not part of this check.
 */
export function inspectBookingFrame(
  frame: HTMLIFrameElement | null,
): BookingFrameInspection {
  if (!frame || typeof window === "undefined") {
    return { visible: false, reason: "missing" };
  }

  const styles = window.getComputedStyle(frame);
  const rect = frame.getBoundingClientRect();
  const opacity = Number.parseFloat(styles.opacity || "1");
  const hiddenStyle =
    styles.display === "none" ||
    styles.visibility === "hidden" ||
    opacity <= 0 ||
    styles.pointerEvents === "none";

  if (hiddenStyle) return { visible: false, reason: "hidden-style" };
  if (rect.width <= 0 || rect.height <= 0) {
    return { visible: false, reason: "zero-size" };
  }

  // The resizer's failed state uses an absolute -9999px placement. This is a
  // direct style/geometry check, not a viewport intersection test.
  const left = Number.parseFloat(styles.left || "0");
  if (styles.position === "absolute" && left < -1000) {
    return { visible: false, reason: "offscreen-style" };
  }

  return { visible: true, reason: "visible" };
}

export function useBookingDisplay(
  frameRef: RefObject<HTMLIFrameElement | null>,
  active: boolean,
): BookingDisplayStatus {
  const [status, setStatus] = useState<BookingDisplayStatus>("inactive");

  useEffect(() => {
    if (!active) {
      setStatus("inactive");
      return;
    }

    let disposed = false;
    let hiddenDeadline: number | undefined;
    let hiddenEpisodeActive = false;
    const startHiddenEpisode = () => {
      if (disposed || hiddenEpisodeActive) return;
      hiddenEpisodeActive = true;
      setStatus("waiting");
      hiddenDeadline = window.setTimeout(() => {
        hiddenDeadline = undefined;
        if (disposed) return;
        const result = inspectBookingFrame(frameRef.current);
        setStatus(result.visible ? "visible" : "blocked");
      }, BOOKING_DISPLAY_GRACE_MS);
    };
    const clearHiddenEpisode = () => {
      if (hiddenDeadline !== undefined) {
        window.clearTimeout(hiddenDeadline);
        hiddenDeadline = undefined;
      }
      hiddenEpisodeActive = false;
    };
    const inspect = () => {
      if (disposed) return;
      if (inspectBookingFrame(frameRef.current).visible) {
        clearHiddenEpisode();
        setStatus("visible");
      } else {
        // A hidden mutation during an active episode never postpones its
        // deadline. Recovery starts a fresh episode only after visibility.
        startHiddenEpisode();
      }
    };

    inspect();
    const frame = frameRef.current;
    const mutationObserver =
      typeof MutationObserver === "undefined" || !frame
        ? null
        : new MutationObserver(inspect);
    mutationObserver?.observe(frame, {
      attributes: true,
      attributeFilter: ["style", "class"],
    });
    const resizeObserver =
      typeof ResizeObserver === "undefined" || !frame
        ? null
        : new ResizeObserver(inspect);
    resizeObserver?.observe(frame);
    frame?.addEventListener("load", inspect);

    return () => {
      disposed = true;
      clearHiddenEpisode();
      mutationObserver?.disconnect();
      resizeObserver?.disconnect();
      frame?.removeEventListener("load", inspect);
    };
  }, [active, frameRef]);

  return status;
}
